// The browser's WebAuthn API takes and gives binary fields; the server's
// ceremonies are JSON, with them in base64url. These turn one into the
// other, as newer browsers' PublicKeyCredential.parse*OptionsFromJSON and
// toJSON do.

type JSONObject = Record<string, unknown>;

export function fromB64(s: string): ArrayBuffer {
  const std = s.replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(std.padEnd(Math.ceil(std.length / 4) * 4, "="));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}

export function toB64(b: ArrayBuffer): string {
  let bin = "";
  for (const x of new Uint8Array(b)) bin += String.fromCharCode(x);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// clean leaves out what the server sent as null, which browsers refuse
// where they expect a dictionary or a list.
function clean(o: JSONObject): JSONObject {
  return Object.fromEntries(Object.entries(o).filter(([, v]) => v !== null && v !== undefined));
}

const descriptors = (list: unknown) => ((list as JSONObject[] | null) ?? []).map((c) => ({ ...clean(c), id: fromB64(c.id as string) }));

// creationOptions are the options to add a passkey with.
export function creationOptions(o: JSONObject): PublicKeyCredentialCreationOptions {
  const user = o.user as JSONObject;
  return {
    ...clean(o),
    challenge: fromB64(o.challenge as string),
    user: { ...clean(user), id: fromB64(user.id as string) },
    excludeCredentials: descriptors(o.excludeCredentials),
  } as PublicKeyCredentialCreationOptions;
}

// requestOptions are the options to sign in with a passkey.
export function requestOptions(o: JSONObject): PublicKeyCredentialRequestOptions {
  return { ...clean(o), challenge: fromB64(o.challenge as string), allowCredentials: descriptors(o.allowCredentials) } as PublicKeyCredentialRequestOptions;
}

// credentialJSON is the browser's answer as the server reads it.
export function credentialJSON(c: PublicKeyCredential): JSONObject {
  const r = c.response as AuthenticatorAttestationResponse & AuthenticatorAssertionResponse;
  const response: JSONObject = { clientDataJSON: toB64(r.clientDataJSON) };
  if ("attestationObject" in r && r.attestationObject) {
    response.attestationObject = toB64(r.attestationObject);
    response.transports = r.getTransports?.() ?? [];
  } else {
    response.authenticatorData = toB64(r.authenticatorData);
    response.signature = toB64(r.signature);
    if (r.userHandle) response.userHandle = toB64(r.userHandle);
  }
  return {
    id: c.id,
    rawId: toB64(c.rawId),
    type: c.type,
    response,
    clientExtensionResults: c.getClientExtensionResults(),
    ...(c.authenticatorAttachment ? { authenticatorAttachment: c.authenticatorAttachment } : {}),
  };
}

// passkeysHere reports whether passkeys work in this browser, at this
// address: the server binds them to its public URL's origin, which methods
// from /auth/methods name.
export const passkeysHere = (methods?: { passkeys?: { origin: string } }) =>
  typeof window !== "undefined" && typeof window.PublicKeyCredential === "function" && methods?.passkeys?.origin === location.origin;

// cancelled reports whether a ceremony ended because the person closed the
// browser's prompt, which is no error to show.
export const cancelled = (err: unknown) => err instanceof DOMException && (err.name === "NotAllowedError" || err.name === "AbortError");
